// NoteTaskBackfillPanel — Settings → Payment Generation (beside Note Summaries).
//
// Harvi reads a note for action items on its own only while the note is under
// 14 days old. This reads OLDER notes once, back to a date the Admin picks, via
// GET/POST /api/note-task-proposals/backfill. It creates no tasks: each proposal
// waits in the ordinary review pop-up for the person who wrote the note.
//
// The POST enqueues one bounded batch and hands back a cursor; this loops it
// until `done`. The reading itself happens on the queue afterwards, so the
// counts are re-read on a poll while anything is still pending.
import { useEffect, useState, useCallback, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { Btn, Card } from '../ui';

const DEFAULT_SINCE = '2026-06-01';
const POLL_MS = 15000;

const field = {
  height: 36, boxSizing: 'border-box', padding: '0 10px', border: `1px solid ${B.border}`,
  borderRadius: 8, font: 'inherit', fontSize: 14, color: B.text, background: B.surface,
};

export function NoteTaskBackfillPanel() {
  const [since, setSince] = useState(DEFAULT_SINCE);
  const [mine, setMine] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [counting, setCounting] = useState(false);
  const [queuing, setQueuing] = useState(null); // { enqueued, failed } while the loop runs
  const [queued, setQueued] = useState(null);   // final totals of the last run
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const count = useCallback(async () => {
    setCounting(true); setError(null);
    try {
      const res = await api.get(`note-task-proposals/backfill?since=${encodeURIComponent(since)}&mine=${mine ? 1 : 0}`, { timeout: 60000 });
      if (alive.current) setStatus(res);
    } catch (e) { if (alive.current) setError(e.message); }
    finally { if (alive.current) setCounting(false); }
  }, [since, mine]);

  // Keep the counts moving while the queue is still reading.
  useEffect(() => {
    if (!status || !(status.pending > 0 || (queued && status.remaining > 0))) return undefined;
    const t = setTimeout(count, POLL_MS);
    return () => clearTimeout(t);
  }, [status, queued, count]);

  const start = async () => {
    setError(null); setQueued(null);
    let after = null, enqueued = 0, failed = 0;
    setQueuing({ enqueued, failed });
    try {
      for (let pass = 0; pass < 500; pass++) {
        const res = await api.post('note-task-proposals/backfill', { since, mine, after }, { timeout: 120000 });
        enqueued += res.enqueued || 0; failed += res.failed || 0;
        if (alive.current) setQueuing({ enqueued, failed });
        if (res.done || !res.nextAfter) break;
        after = res.nextAfter;
      }
      if (alive.current) setQueued({ enqueued, failed });
    } catch (e) {
      if (alive.current) { setError(e.message); setQueued({ enqueued, failed, stopped: true }); }
    } finally {
      if (alive.current) setQueuing(null);
      count();
    }
  };

  const busy = counting || !!queuing;

  return (
    <Card style={{ marginTop: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Find tasks in older notes</div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16, lineHeight: 1.5 }}>
        Harvi reads notes from the last 14 days on its own. This reads older notes once, back to the date you pick.
        It creates no tasks — each proposal waits for the person who wrote the note to add or dismiss it.
        Notes attached to a task, merged notes, and notes already read are skipped, so it is safe to run again.
        Each note is one AI read.
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: B.text }}>
          Read notes created on or after
          <input type="date" value={since} onChange={e => { setSince(e.target.value); setStatus(null); }} style={field} disabled={busy} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: B.text }}>
          Whose notes
          <select value={mine ? 'mine' : 'all'} onChange={e => { setMine(e.target.value === 'mine'); setStatus(null); }} style={field} disabled={busy}>
            <option value="all">Everyone's</option>
            <option value="mine">Only mine</option>
          </select>
        </label>
        <Btn size="sm" variant="secondary" disabled={busy || !since} onClick={count}>
          {counting ? 'Counting…' : 'Count notes'}
        </Btn>
        <Btn size="sm" disabled={busy || !status || !status.available || status.remaining === 0} onClick={start}>
          {queuing ? `Queuing… ${queuing.enqueued}` : 'Start reading'}
        </Btn>
      </div>

      {error && <div style={{ fontSize: 13, color: B.red, marginBottom: 8 }}>⚠ {error}</div>}

      {status && !status.available && (
        <div style={{ fontSize: 13, color: B.red }}>⚠ {status.reason || 'Task proposals are not available on this database.'}</div>
      )}

      {status && status.available && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Stat label="Not read yet" value={status.remaining} sub={`across ${status.deals} deal${status.deals === 1 ? '' : 's'}`} />
          <Stat label="Being read" value={status.pending} />
          <Stat label="Read" value={status.read} sub={status.errors ? `${status.errors} failed` : null} />
          <Stat label="Proposals waiting" value={status.openProposals} sub={`on ${status.withProposals} note${status.withProposals === 1 ? '' : 's'}`} />
        </div>
      )}

      {queued && (
        <div style={{ marginTop: 12, fontSize: 13, color: queued.failed || queued.stopped ? B.red : B.green }}>
          {queued.stopped ? '⚠ Stopped part way — ' : '✓ '}
          Queued {queued.enqueued} note{queued.enqueued === 1 ? '' : 's'}{queued.failed ? `, ${queued.failed} could not be queued` : ''}.
          {' '}They're read in the background; the counts above refresh while that runs.
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div style={{ flex: '1 1 140px', padding: '10px 14px', border: `1px solid ${B.divider}`, borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: B.textMd }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: B.text }}>{value ?? 0}</div>
      {sub && <div style={{ fontSize: 12, color: B.textLt }}>{sub}</div>}
    </div>
  );
}
